export type Subscriber<T> = (state: T, previous: T, changed: (keyof T)[]) => void;
export interface Store<T> { readonly state: T; patch(partial: Partial<T>): void; subscribe(subscriber: Subscriber<T>): () => boolean }
/**
 * A very small observable store.
 *
 * The whole app is driven by seven or eight scalars, so this is deliberately
 * not a state library: create it with every key it will ever hold, patch it,
 * and subscribers are told what changed.
 */

/** @param initial every key the store will ever hold */
export function createStore<T extends object>(initial: T) {
  const state = { ...initial };
  const subscribers = new Set<Subscriber<T>>();

  return {
    get state() {
      return state;
    },

    /** Merge a partial update. Subscribers hear about it only if it changed something. */
    patch(partial: Partial<T>) {
      const changed: (keyof T)[] = [];

      for (const [key, value] of Object.entries(partial)) {
        if (!(key in state)) throw new Error(`unknown state key: ${key}`);
        if (Object.is(state[key as keyof T], value)) continue;
        changed.push(key as keyof T);
      }

      if (changed.length === 0) return;

      const previous = { ...state };
      Object.assign(state, partial);

      for (const subscriber of subscribers) {
        try {
          subscriber(state, previous, changed);
        } catch (error) {
          // One broken listener must not stop the rest of the frame.
          console.error("store subscriber failed", error);
        }
      }
    },

    /** @returns a function that removes the subscription */
    subscribe(subscriber: Subscriber<T>) {
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },
  };
}
